/**
 * Member setting storage and server syncing.
 * @constructor
 * @extends {Foundation.Observable}
 * @param {string} memberOid
 * @param {string} namespace
 * @param {string} name
 */
Foundation.Member.Setting = function (memberOid, namespace, name) {
    Foundation.Observable.call(this);

    /**
     * @private
     * @type {string}
     */
    this._memberOid = memberOid;

    if (this._memberOid.match(/\D/)) {
        throw new Error("Bad memberOid parameter.");
    }

    /**
     * @private
     * @type {string}
     */
    this._namespace = namespace;

    /**
     * @private
     * @type {string}
     */
    this._name = name;

    // Get notifications when something in this namespace changes.
    var settings = Foundation.Member.Setting.storableForNamespace(this._namespace);
    settings.observe("changed", this.handleNamespaceChange.bind(this));
};
Foundation.inherit(Foundation.Member.Setting, Foundation.Observable);

/**
 * @public
 * @return {string}
 */
Foundation.Member.Setting.prototype.value = function () {
    var settings = Foundation.Member.Setting.storableForNamespace(this._namespace);
    return /** @type {string} */ (settings.get(`${this._memberOid}.${this._name}`)) || "";
};

Foundation.Member.Setting.prototype.toString = Foundation.Member.Setting.prototype.value;

/**
 * @public
 * @param {string} value
 */
Foundation.Member.Setting.prototype.setValue = function (value) {
    value += "";

    if (value.length > 255) {
        throw new Error(`Failed to set Foundation.Member.Setting value, too large: ${value}`);
    }

    var settings = Foundation.Member.Setting.storableForNamespace(this._namespace);
    settings.set(`${this._memberOid}.${this._name}`, value);

    new Foundation.API("PUT", "MemberSetting", {
        "memberOid": this._memberOid,
        "namespace": this._namespace,
        "name": this._name,
        "value": value
    });
};

/**
 * If a setting changes in this namespace, this method is called so we can
 * fire a changed event on ourselves.
 * @private
 * @param {Foundation.Observable.Event=} e
 */
Foundation.Member.Setting.prototype.handleNamespaceChange = function (e) {
    if (e.data && e.data["field"] === `${this._memberOid}.${this._name}`) {
        this.fire("changed");
    }
};

/**
 * @param {string|number} memberOid
 * @param {string} namespace
 * @return {Foundation.Promise}
 */
Foundation.Member.Setting.updateNamespace = function (memberOid, namespace) {
    var promise;
    var settings = Foundation.Member.Setting.storableForNamespace(namespace);

    if (settings.secondsSinceLastSave() > 30 * 60) {
        var response = new Foundation.API("GET", "MemberSetting", {
            "memberOid": memberOid,
            "namespace": namespace
        });

        response.then(function () {
            response.fields().forEach(function (namespace) {
                var values = response.get(namespace);
                for (var name in values) {
                    settings.set(`${memberOid}.${name}`, values[name]);
                }
            })
        });

        promise = response;
    }
    else {
        promise = (new Foundation.Promise()).fulfill();
    }

    return promise;
};

/**
 * @param {string} namespace
 * @return {Foundation.Storable}
 */
Foundation.Member.Setting.storableForNamespace = function (namespace) {
    if (!Foundation.Member.Setting._storables) {
        /** @type {Object.<string,Foundation.Storable>} */
        Foundation.Member.Setting._storables = {};
    }

    if (!Foundation.Member.Setting._storables[namespace]) {
        var bucket = Foundation.Member.Setting.sharedStorableBucket();
        var settings = bucket.getStorable(namespace);

        if (!settings) {
            settings = new Foundation.Storable(namespace);
            bucket.saveStorable(settings);
        }

        // Set up auto saving.
        settings.observeCoalesced("changed", function () {
            bucket.saveStorable(settings)
        });

        Foundation.Member.Setting._storables[namespace] = settings;
    }

    return Foundation.Member.Setting._storables[namespace];
}

/**
 * @return {Foundation.Storable.Bucket}
 */
Foundation.Member.Setting.sharedStorableBucket = function () {
    if (!Foundation.Member.Setting._sharedStorableBucket) {
        Foundation.Member.Setting._sharedStorableBucket = new Foundation.Storable.Bucket(
            "Member.Setting", Foundation.Storable.Bucket.Persistence.SESSION
        );

        // Clear this bucket on logout.
        Foundation.Session.Manager.sharedInstance().observe("logout", function () {
            Foundation.Member.Setting._sharedStorableBucket.clear();
            Foundation.Member.Setting._sharedStorableBucket = null;
        });
    }
    return Foundation.Member.Setting._sharedStorableBucket;
}

Foundation.Member.Setting.prototype["value"] = Foundation.Member.Setting.prototype.value;
Foundation.Member.Setting.prototype["setValue"] = Foundation.Member.Setting.prototype.setValue;

